import React, { useEffect, useState } from "react";
import AppButton from "components/AppButton";
import { getBusinessConnectionStatus } from "pages/FbManagerAccess/functions";
import { Oval } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faCheckCircle,
  faCircleXmark,
  faCopy,
  faExternalLinkAlt,
  faLightbulb,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import AppText from "components/AppText";
import { formatMessageInPlaceholder } from "functions/global";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

const Tip = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-primary-text/10 bg-primary-soft/70 px-3.5 py-3">
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface text-primary-text shadow-sm">
      <FontAwesomeIcon icon={faLightbulb as IconProp} className="text-[11px]" />
    </span>
    <p className="m-0 text-[0.84rem] leading-relaxed text-ink-secondary">
      {children}
    </p>
  </div>
);

const ReferenceImage = ({
  src,
  label,
}: {
  src: string;
  label: string;
}) => (
  <div className="mt-3 overflow-hidden rounded-2xl border border-line bg-gradient-to-b from-muted to-surface shadow-sm">
    <div className="flex items-center justify-between border-b border-line/80 px-3.5 py-2.5">
      <span className="text-[0.7rem] font-bold uppercase tracking-[0.06em] text-ink-muted">
        <AppText>Visual guide</AppText>
      </span>
      <span className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-[0.68rem] font-bold text-primary-text">
        <AppText>{label}</AppText>
      </span>
    </div>
    <div className="bg-[linear-gradient(45deg,var(--bg-muted)_25%,transparent_25%),linear-gradient(-45deg,var(--bg-muted)_25%,transparent_25%),linear-gradient(45deg,transparent_75%,var(--bg-muted)_75%),linear-gradient(-45deg,transparent_75%,var(--bg-muted)_75%)] bg-[length:16px_16px] bg-[position:0_0,0_8px,8px_-8px,-8px_0] p-3">
      <img
        alt=""
        src={src}
        className="mx-auto block max-h-[250px] w-full rounded-xl border border-line bg-surface object-contain shadow-sm"
      />
    </div>
  </div>
);

const InstructionStep = (props: any) => {
  const {
    selectedFbAccount,
    adminDetails,
    fbAccessToken,
    connectId,
    workspaceId,
    selectedFbPage,
    setActiveStep,
    setConfirmAccess,
    onBack,
  } = props;

  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyTextToClipboard = () => {
    navigator.clipboard
      .writeText(adminDetails?.businessManagerId || "")
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      })
      .catch((err) => console.error("Failed to copy:", err));
  };

  const openBusinessManager = () => {
    window.open(
      `https://business.facebook.com/settings/partners/?business_id=${selectedFbAccount?.business?.id}`
    );
  };

  const checkStatus = async () => {
    try {
      setLoading(true);
      const nextStatus = await getBusinessConnectionStatus({
        accessToken: fbAccessToken,
        adAccountId: selectedFbAccount?.id,
        pageId: selectedFbPage?.id || "",
        adminDbId: connectId,
        ...(workspaceId ? { workspaceId } : {}),
      });
      setStatus(nextStatus);
    } catch (error) {
      setStatus(false);
      console.error("Error occurred while fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToConnectedPage = () => {
    setConfirmAccess(true);
    setActiveStep(2);
  };

  const goBackToAccountSelection = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    setConfirmAccess(false);
  };

  const steps = [
    {
      id: 1,
      title: "Copy your partner's Business ID",
      body: (
        <>
          <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-line bg-surface p-3.5 shadow-sm sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1 rounded-xl bg-muted px-3.5 py-3">
              <p className="m-0 mb-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] text-ink-muted">
                <AppText>Partner Business ID</AppText>
              </p>
              <code className="break-all font-mono text-[0.98rem] font-semibold tracking-[0.02em] text-ink">
                {adminDetails?.businessManagerId}
              </code>
            </div>
            <button
              type="button"
              onClick={copyTextToClipboard}
              className={`inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-[10px] px-4 text-[0.86rem] font-semibold transition ${
                copied
                  ? "bg-success-soft text-success ring-1 ring-success/20"
                  : "bg-primary text-white shadow-btn hover:bg-primary-hover"
              }`}
            >
              <FontAwesomeIcon
                icon={(copied ? faCheckCircle : faCopy) as IconProp}
              />
              {copied ? (
                formatMessageInPlaceholder("Copied!")
              ) : (
                <AppText>Copy ID</AppText>
              )}
            </button>
          </div>
          <Tip>
            <AppText>
              Keep this ID handy — you’ll paste it into Meta in a later step.
            </AppText>
          </Tip>
        </>
      ),
    },
    {
      id: 2,
      title: "Open your Business Manager",
      body: (
        <>
          <p className="mt-2 mb-0 text-[0.86rem] leading-relaxed text-ink-secondary">
            <AppText>
              Open Partners settings in a new tab. Leave this page open so you
              can verify access when you return.
            </AppText>
          </p>
          <button
            type="button"
            onClick={openBusinessManager}
            className="mt-3 inline-flex h-11 items-center gap-2 rounded-[10px] bg-primary px-5 text-[0.9rem] font-semibold text-white shadow-btn transition hover:bg-primary-hover"
          >
            <AppText>Open Business Manager</AppText>
            <FontAwesomeIcon
              icon={faExternalLinkAlt as IconProp}
              className="text-[11px]"
            />
          </button>
        </>
      ),
    },
    {
      id: 3,
      title: 'Click "Add" under partner access',
      body: (
        <>
          <p className="mt-2 mb-0 text-[0.86rem] leading-relaxed text-ink-secondary">
            <AppText
              values={{
                add: <b className="text-ink">"Add"</b>,
                shareAssets: (
                  <b className="text-ink">
                    "Give a partner access to your assets"
                  </b>
                ),
              }}
            >
              {`In Meta, open the blue dropdown {add} under {shareAssets}.`}
            </AppText>
          </p>
          <ReferenceImage
            src="images/fb-ad-connector/step_3.png"
            label="Step 3"
          />
        </>
      ),
    },
    {
      id: 4,
      title: "Paste the copied Business Manager ID",
      body: (
        <Tip>
          <AppText>
            Paste the partner ID from step 1 into Meta’s invite field, then
            continue.
          </AppText>
        </Tip>
      ),
    },
    {
      id: 5,
      title: "Assign ad account permissions",
      body: (
        <>
          <p className="mt-2 mb-0 text-[0.86rem] leading-relaxed text-ink-secondary">
            <AppText
              values={{
                adAccounts: <b className="text-ink">"Ad Accounts"</b>,
                manageAdAccounts: (
                  <b className="text-ink">"Manage ad accounts"</b>
                ),
              }}
            >
              {`Select {adAccounts} on the left, choose your account(s), and turn on {manageAdAccounts}.`}
            </AppText>
          </p>
          <ReferenceImage
            src="images/fb-ad-connector/step_5.png"
            label="Step 5"
          />
        </>
      ),
    },
    {
      id: 6,
      title: "Save changes in Meta",
      body: (
        <Tip>
          <AppText>
            Click Save Changes in Meta, then come back here and verify access to
            finish.
          </AppText>
        </Tip>
      ),
    },
  ];

  return (
    <div className="relative z-10 space-y-5 animate-lb-fade-up">
      <div className="relative overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-primary-soft via-surface to-surface p-5 shadow-card sm:p-6">
        <div
          className="pointer-events-none absolute -right-12 -top-14 h-44 w-44 rounded-full bg-primary/10 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-16 left-8 h-32 w-32 rounded-full bg-primary/5 blur-2xl"
          aria-hidden
        />

        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 max-w-xl">
            <button
              type="button"
              onClick={goBackToAccountSelection}
              className="mb-3 inline-flex cursor-pointer items-center gap-2 rounded-[10px] border border-line bg-surface px-3 py-2 text-[0.875rem] font-semibold text-ink shadow-sm transition hover:bg-muted hover:text-ink"
            >
              <FontAwesomeIcon
                icon={faArrowLeft as IconProp}
                className="text-[12px] text-ink"
              />
              <AppText>Back</AppText>
            </button>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface/95 px-3 py-1 text-[0.72rem] font-bold text-primary-text shadow-sm">
                <img
                  src="images/global/meta.png"
                  alt=""
                  className="h-3.5 w-3.5 object-contain"
                />
                <AppText>Final setup in Meta</AppText>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface/80 px-2.5 py-1 text-[0.7rem] font-semibold text-ink-secondary">
                <FontAwesomeIcon
                  icon={faShieldHalved as IconProp}
                  className="text-[10px] text-primary-text"
                />
                <AppText>Secure partner share</AppText>
              </span>
            </div>

            <p className="m-0 mb-2 text-[1.4rem] font-bold tracking-[-0.03em] text-ink">
              <AppText>You're almost done!</AppText>
            </p>
            <p className="m-0 text-[0.92rem] leading-relaxed text-ink-secondary">
              <AppText>
                Complete the checklist below in Meta Business Manager, then
                verify access to continue.
              </AppText>
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-lg border border-line bg-surface/90 px-2.5 py-1.5 text-[0.75rem] text-ink-secondary shadow-sm">
                <span className="font-semibold text-ink">
                  <AppText>Ad account</AppText>:
                </span>{" "}
                {selectedFbAccount?.name || "—"}
              </span>
              {selectedFbAccount?.business?.name ? (
                <span className="rounded-lg border border-line bg-surface/90 px-2.5 py-1.5 text-[0.75rem] text-ink-secondary shadow-sm">
                  <span className="font-semibold text-ink">
                    <AppText>Business</AppText>:
                  </span>{" "}
                  {selectedFbAccount.business.name}
                </span>
              ) : null}
            </div>
          </div>

          <div
            className={`w-full shrink-0 rounded-2xl border px-4 py-3.5 backdrop-blur-sm sm:w-[220px] ${
              status
                ? "border-success/25 bg-success-soft shadow-sm"
                : "border-line bg-surface/95 shadow-soft"
            }`}
          >
            <p className="m-0 mb-2 text-[0.68rem] font-bold uppercase tracking-[0.06em] text-ink-muted">
              <AppText>Live status</AppText>
            </p>
            {loading ? (
              <div className="flex items-center gap-2">
                <Oval
                  height={20}
                  width={20}
                  color="var(--color-primary)"
                  visible
                  ariaLabel="oval-loading"
                  secondaryColor="var(--text-grey)"
                  strokeWidth={2}
                  strokeWidthSecondary={2}
                />
                <span className="text-[0.84rem] text-ink-secondary">
                  <AppText>Checking...</AppText>
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full ${
                    status ? "bg-surface text-success" : "bg-muted text-danger"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={(status ? faCheckCircle : faCircleXmark) as IconProp}
                    className="text-[18px]"
                  />
                </span>
                <div>
                  <p
                    className={`m-0 text-[1rem] font-bold leading-tight ${
                      status ? "text-success" : "text-ink"
                    }`}
                  >
                    <AppText>{status ? "Granted" : "Pending"}</AppText>
                  </p>
                  <p className="m-0 mt-0.5 text-[0.72rem] text-ink-muted">
                    <AppText>
                      {status ? "Ready to continue" : "Waiting on Meta"}
                    </AppText>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
        <div className="flex items-center justify-between gap-3 border-b border-line bg-gradient-to-r from-muted/90 to-surface px-5 py-4">
          <div>
            <p className="m-0 text-[1rem] font-bold tracking-[-0.01em] text-ink">
              <AppText>Grant access checklist</AppText>
            </p>
            <p className="m-0 mt-0.5 text-[0.78rem] text-ink-muted">
              <AppText>Follow these steps in Meta Business Manager</AppText>
            </p>
          </div>
          <span className="inline-flex items-center rounded-full border border-line bg-surface px-2.5 py-1 text-[0.72rem] font-bold text-ink-secondary shadow-sm">
            6 <AppText>steps</AppText>
          </span>
        </div>

        <div className="relative px-4 py-1 sm:px-6 sm:py-2">
          <div
            className="absolute bottom-10 left-[33px] top-10 w-px bg-gradient-to-b from-primary via-primary/35 to-line sm:left-[41px]"
            aria-hidden
          />

          <ol className="relative m-0 list-none p-0">
            {steps.map((step, index) => (
              <li
                key={step.id}
                className={`group relative flex gap-3.5 py-5 transition sm:gap-4 ${
                  index !== steps.length - 1 ? "border-b border-line/70" : ""
                }`}
              >
                <span className="relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-[0.82rem] font-bold text-white shadow-[0_0_0_5px_var(--bg-surface),0_4px_10px_rgba(15,96,253,0.25)] transition group-hover:scale-105 sm:h-9 sm:w-9">
                  {step.id}
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="m-0 text-[1rem] font-bold tracking-[-0.015em] text-ink">
                    <AppText>{step.title}</AppText>
                  </p>
                  {step.body}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div
        className={`rounded-2xl border p-4 shadow-sm sm:p-5 ${
          status
            ? "border-success/20 bg-gradient-to-r from-success-soft to-surface"
            : "border-line bg-gradient-to-r from-surface via-surface to-primary-soft/40"
        }`}
      >
        <div className="flex flex-col gap-3.5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="m-0 flex items-center gap-2 text-[0.98rem] font-bold text-ink">
              {status ? (
                <FontAwesomeIcon
                  icon={faCheckCircle as IconProp}
                  className="text-success"
                />
              ) : null}
              <AppText>
                {status ? "Access confirmed" : "Ready to verify?"}
              </AppText>
            </p>
            <p className="m-0 mt-1 text-[0.84rem] leading-relaxed text-ink-secondary">
              <AppText>
                {status
                  ? "Partner access looks good. Continue to finish setup."
                  : "Finish the Meta steps, then check access to continue."}
              </AppText>
            </p>
          </div>
          <AppButton
            onClick={() => (status ? goToConnectedPage() : checkStatus())}
            className="fb-manager-access-btn !inline-flex !h-11 !w-full !items-center !justify-center !gap-2 !rounded-[10px] !bg-primary !px-5 !font-semibold !text-white !shadow-btn sm:!w-auto"
            title={status ? "CONTINUE" : "CHECK ACCESS"}
            loading={loading}
          />
        </div>
        {!status && (
          <p className="mt-3 mb-0 flex items-center gap-1.5 text-[0.75rem] text-ink-muted">
            <FontAwesomeIcon
              icon={faArrowRight as IconProp}
              className="text-[10px]"
            />
            <AppText>
              Tip: after saving in Meta, click Check Access right away.
            </AppText>
          </p>
        )}
      </div>
    </div>
  );
};

export default InstructionStep;
